import {useState} from 'react';
import {Image} from '@shopify/hydrogen';
import clsx from 'clsx';
import {startOfDay} from 'date-fns';
import {formatInTimeZone} from 'date-fns-tz';
import Calendar from 'react-calendar';
import {IVariantTimeslot} from '~/lib/types/graphql/api';
import {
  DISPLAY_TIMEZONE,
  formatNumber,
  getWhatsappUrlWithText,
  scaleWidth,
} from '~/utils';
import {RoundedModal} from '..';
import WheelPicker, {PickerData} from 'react-simple-wheel-picker';
import {localTimezone} from '~/utils/tz';

interface Props {
  date: Date;
  productTitle: string;
  timeslotsError: any;
  selectedVariant: any;
  selectedSellingPlan: any;
  isLoadingTimeslots: boolean;
  onSetDate: (date: Date) => void;
  timeslots: IVariantTimeslot[] | undefined;
  selectedTimeslot: IVariantTimeslot | undefined;
  onSetSelectedTimeslot: (timeslot: IVariantTimeslot | undefined) => void;
}

export function ProductSelectTimeslot({
  date,
  productTitle,
  onSetDate,
  timeslots,
  selectedVariant,
  selectedTimeslot,
  isLoadingTimeslots,
  onSetSelectedTimeslot,
}: Props) {
  const getDateList = (pDate: Date) => {
    const dateList = [];
    for (let i = -60; i <= 60; i++) {
      const vDate = new Date(pDate.getTime());
      vDate.setDate(vDate.getDate() + i);
      dateList.push(vDate);
    }
    return dateList.map((item) => {
      const isToday =
        startOfDay(item).getTime() == startOfDay(new Date()).getTime();
      const startDay = startOfDay(item);
      return {
        id: startDay.toString(),
        value: isToday
          ? 'Today'
          : formatInTimeZone(startDay, localTimezone, 'EEE d LLL'),
      };
    });
  };

  const getHourList = () => {
    return new Array(24)
      .fill(0)
      .map((item, index) => index)
      .map((item) => ({
        id: item + '',
        value: item.toString().padStart(2, '0'),
      }));
  };

  const getMinuteList = () => {
    return [0, 30].map((item) => ({
      id: item + '',
      value: item.toString().padStart(2, '0'),
    }));
  };

  const updateDateList = (pDate: string) => {
    const currentDate = window.localStorage.getItem('whatsappDate');
    if (pDate === currentDate) {
      setDateList(getDateList(new Date(pDate)));
    }
  };

  const today = startOfDay(new Date());
  const [whatsappModal, setWhatsappModal] = useState<boolean>(false);
  const [whatsappDate, setWhatsappDate] = useState<Date>(new Date());
  const [whatsappHour, setWhatsappHour] = useState<number>(7);
  const [whatsappMinute, setWhatsappMinute] = useState<number>(0);
  const [dateList, setDateList] = useState<PickerData[]>(
    getDateList(new Date()),
  );

  if (!selectedVariant) {
    return <></>;
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <Calendar
          className="tfr tfr-mobile !w-full"
          value={date}
          minDate={today}
          onChange={(pDate: Date) => {
            onSetSelectedTimeslot(undefined);
            onSetDate(pDate);
          }}
        />
      </div>
      <div className="border-t-[0.5px] border-acai-900 pt-6 md:border-l-[0.5px] md:border-t-[0px] md:pl-4 md:pt-0">
        {isLoadingTimeslots && (
          <div className="text-sm">Loading timeslots...</div>
        )}
        {!isLoadingTimeslots && timeslots?.length === 0 && (
          <div className="text-sm">No timeslots available.</div>
        )}
        {!isLoadingTimeslots && timeslots && timeslots?.length > 0 && date && (
          <div className="md:h-[300px]">
            <div className="grid grid-cols-2 gap-2">
              {timeslots.map((timeslot) => (
                <button
                  key={timeslot.dateTime}
                  className={clsx(
                    'relative flex w-full items-center justify-center rounded border-[0.5px] border-acai-900 px-[10px] py-4 font-normal',
                    selectedTimeslot &&
                      selectedTimeslot?.dateTime === timeslot.dateTime &&
                      'bg-acai-900 text-white',
                    (!selectedTimeslot ||
                      (selectedTimeslot &&
                        selectedTimeslot?.dateTime !== timeslot.dateTime)) &&
                      'bg-white text-acai-900',
                  )}
                  onClick={() => {
                    onSetSelectedTimeslot(timeslot);
                  }}
                  style={{fontSize: scaleWidth(14)}}
                >
                  <div style={{letterSpacing: -scaleWidth(0.24)}}>
                    {formatInTimeZone(
                      new Date(timeslot.dateTime),
                      DISPLAY_TIMEZONE,
                      'HH:mm',
                    )}
                  </div>
                  {
                    //We only do surge fees for one-time bookings, ie no selling plan selected
                    timeslot.surgeFee && (
                      <div
                        className={clsx(
                          'absolute rounded-[10px] bg-[#F3EFE9] p-1 leading-[100%]',
                          timeslot === selectedTimeslot
                            ? 'text-acai-900'
                            : 'text-daybreak-400',
                        )}
                        style={{
                          fontSize: scaleWidth(12),
                          right: scaleWidth(15),
                          letterSpacing: -scaleWidth(0.24),
                        }}
                      >
                        + ${`${formatNumber(timeslot.surgeFee.amount).trim()}`}
                      </div>
                    )
                  }
                </button>
              ))}
            </div>
          </div>
        )}
        <RoundedModal
          isOpen={whatsappModal}
          onClose={() => {
            setWhatsappModal(false);
          }}
          onConfirm={() => {
            window.open(
              getWhatsappUrlWithText(
                `Hey TFR team,%0D%0A%0D%0AI would like to book a ${productTitle} on ${formatInTimeZone(
                  whatsappDate,
                  localTimezone,
                  'EEE d LLL',
                )}, ${whatsappHour.toString().padStart(2, '0')}:${whatsappMinute
                  .toString()
                  .padStart(2, '0')}.%0D%0APlease let me know!`,
              ),
              '_blank',
            );
            setWhatsappModal(false);
          }}
          confirmButtonText="Send WhatsApp"
        >
          <div className="px-6 py-6">
            <div className="text-center text-[12px] text-acai-900">
              Choose your preferred date and timing, and we&apos;ll get back to
              you as soon as we can.
            </div>
            <div className="relative">
              <div className="absolute top-[60px] h-[1px] w-full bg-acai-900" />
              <div className="absolute top-[90px] h-[1px] w-full bg-acai-900" />
              <div className="pointer-events-none absolute top-0 z-10 h-[30px] w-full bg-foam-100 opacity-50"></div>
              <div className="pointer-events-none absolute top-[120px] z-10 h-[30px] w-full bg-foam-100 opacity-50"></div>
              <div className="whatsapp-datetime-wheelpicker mt-6 flex items-center justify-around">
                <WheelPicker
                  key={dateList[0].id}
                  data={dateList}
                  onChange={(pDate) => {
                    if (pDate.id !== whatsappDate.toString()) {
                      setWhatsappDate(new Date(pDate.id));
                      window.localStorage.setItem('whatsappDate', pDate.id);
                      setTimeout(() => {
                        updateDateList(pDate.id);
                      }, 300);
                    }
                  }}
                  height={150}
                  width={120}
                  titleText="whatsapp-date-wheelpicker"
                  itemHeight={30}
                  selectedID={startOfDay(whatsappDate).toString()}
                  color="#9C9D9F"
                  fontSize={16}
                  activeColor="#593B37"
                  shadowColor="none"
                  backgroundColor="transparent"
                />
                <WheelPicker
                  data={getHourList()}
                  onChange={(pHour) => {
                    setWhatsappHour(parseInt(pHour.id));
                  }}
                  height={150}
                  width={30}
                  titleText="whatsapp-hour-wheelpicker"
                  itemHeight={30}
                  selectedID={whatsappHour.toString()}
                  color="#9C9D9F"
                  fontSize={16}
                  activeColor="#593B37"
                  shadowColor="none"
                  backgroundColor="transparent"
                />
                <div className="text-[16px] leading-[16px]">:</div>
                <WheelPicker
                  data={getMinuteList()}
                  onChange={(pMinute) => {
                    setWhatsappMinute(parseInt(pMinute.id));
                  }}
                  height={150}
                  width={30}
                  titleText="whatsapp-minute-wheelpicker"
                  itemHeight={30}
                  selectedID={whatsappMinute.toString()}
                  color="#9C9D9F"
                  fontSize={16}
                  activeColor="#593B37"
                  shadowColor="none"
                  backgroundColor="transparent"
                />
              </div>
            </div>
          </div>
        </RoundedModal>
      </div>

      <WhatsappTimeslotMessage
        onClick={() => {
          setWhatsappModal(true);
        }}
      />
    </div>
  );
}

interface WhatsappTimeslotMessage {
  onClick: () => void;
}

const WhatsappTimeslotMessage = ({onClick}: WhatsappTimeslotMessage) => (
  <button
    onClick={onClick}
    className="flex w-full items-center justify-center gap-[4px] rounded-full bg-foam-200 px-[12px] py-[8px] text-sm tracking-tight"
    style={{fontSize: scaleWidth(13)}}
  >
    <Image
      src="/ic_whatsapp.png"
      alt="whatsapp-icon"
      width={scaleWidth(14)}
      height={scaleWidth(13)}
    />
    <span className="text-acai-900">Looking for alternative timing?</span>
    <span
      className="font-semibold italic text-[#686144]"
      style={{fontFamily: 'GT-Alpina'}}
    >
      WhatsApp us!
    </span>
  </button>
);
